/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import {
    ChevronRight,
    Clock,
    PlusCircle,
    RefreshCw,
    Trash2,
    Search,
    Filter,
    X,
    Edit2,
    CheckCircle,
    Eye
} from 'lucide-react';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import ContextWarning from '../components/ContextWarning';
import { usePermissions } from '../context/PermissionContext';
import Pagination from '../components/Pagination';
import { resolveCustomerReferenceLabel } from '../utils/customerReferenceLabel';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

// Named constants 
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

const statusTabs = ['all', 'created', 'opened', 'inProgress', 'completed', 'expired'];
const tabLabels = {
    all: 'All',
    created: 'Created',
    opened: 'Opened',
    inProgress: 'In Progress',
    completed: 'Completed',
    expired: 'Expired'
};

const Assignments = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { can } = usePermissions();

    const [assignments, setAssignments] = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(() => {
        if (location.state && location.state.status) {
            return location.state.status;
        }
        return 'all';
    });
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        workflow: '',
        date: '',
        customerReferenceNumber: ''
    });
    const [tempFilters, setTempFilters] = useState({
        workflow: '',
        date: '',
        customerReferenceNumber: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [workflows, setWorkflows] = useState([]);
    const [isWorkflowDropdownOpen, setIsWorkflowDropdownOpen] = useState(false);
    const [workflowSearch, setWorkflowSearch] = useState('');
    const workflowRef = useRef(null);
    const workflowSearchRef = useRef(null);
    const [searchError, setSearchError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(12); // Items per page

    const fetchCounts = useCallback(async (currentFilters = {}) => {
        try {
            const response = await apiClient.get('/assignments/status-counts', {
                params: {
                    search: currentFilters.search || '',
                    workflow: currentFilters.workflow || '',
                    date: currentFilters.date || '',
                    customerReferenceNumber: currentFilters.customerReferenceNumber || '',
                }
            });
            setStatusCounts(response.data);
        } catch (err) {
            // silently ignore count errors
        }
    }, []);

    const fetchAssignments = useCallback(async (page = currentPage) => {
        // Validation: Search at least 3 characters unless it's empty to clear searching
        const isSearchTooShort = filters.search.trim().length > 0 && filters.search.trim().length < SEARCH_MIN_LENGTH;

        try {
            setLoading(true);
            setError('');
            const [response] = await Promise.all([
                apiClient.get('/assignments', {
                    params: {
                        search: isSearchTooShort ? '' : filters.search,
                        workflow: filters.workflow,
                        date: filters.date,
                        customerReferenceNumber: filters.customerReferenceNumber,
                        status: activeTab === 'all' ? "All" : activeTab,
                        page: page,
                        limit: limit
                    }
                }),
                fetchCounts(filters)
            ]);
            setAssignments(response.data.data);
            setTotalPages(response.data.totalPages);
            setCurrentPage(response.data.page);
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load assignments');
        } finally {
            setLoading(false);
        }
    }, [filters, activeTab, fetchCounts, limit, currentPage]);

    useEffect(() => {
        if (location.state && location.state.status) {
            setActiveTab(location.state.status);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.state, navigate, location.pathname]);

    useEffect(() => {
        // Reset to page 1 when filters or tab change
        setCurrentPage(1);
    }, [filters.workflow, filters.date, filters.customerReferenceNumber, filters.search, activeTab]);

    useEffect(() => {
        // Skip API call if search is typed but too short (1-2 chars)
        const isSearchTooShort = filters.search.trim().length > 0 && filters.search.trim().length < SEARCH_MIN_LENGTH;
        if (isSearchTooShort) { return; }

        const timeout = setTimeout(() => {
            fetchAssignments(currentPage);
            setSearchError(''); // Clear search errors on successful triggers
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [fetchAssignments, filters.search, currentPage]);

    useEffect(() => {
        const fetchWorkflows = async () => {
            try {
                const response = await apiClient.get('/workflows', { params: { limit: 1000 } });
                const data = response.data;
                if (Array.isArray(data)) {
                    setWorkflows(data);
                } else if (data && Array.isArray(data.workflows)) {
                    setWorkflows(data.workflows);
                } else {
                    setWorkflows([]);
                }
            } catch (err) {
                console.error('Failed to load workflows', err);
                setWorkflows([]);
            }
        };
        fetchWorkflows();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (workflowRef.current && !workflowRef.current.contains(event.target)) {
                setIsWorkflowDropdownOpen(false);
                setWorkflowSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = assignments;

    const tabCounts = statusCounts;

    const handleDelete = async (assignmentId) => {
        setDeleting(true);
        try {
            await apiClient.delete(`/assignments/${assignmentId}`);
            setAssignments(prev => prev.filter(a => a.id !== assignmentId));
            fetchCounts();
            setError('');
            setDeleteConfirm(null);
            toastService.success('Assignment deleted successfully');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to delete assignment';
            toastService.error(msg)
            setDeleteConfirm(null);
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteClick = (e, assignment) => {
        e.stopPropagation();
        setError(''); // Clear any previous errors
        setDeleteConfirm(assignment);
    };

    const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-4">
                <div>
                    <h1 className="page-title">Assignments</h1>
                    <p className="page-subtitle">Track and manage all media capture sessions</p>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                        onClick={fetchAssignments}
                        className="btn btn-square btn-ghost bg-base-content/5 border border-base-content/10 hover:bg-base-content/10 rounded-xl transition-all"
                        title="Refresh"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    </button>
                    {can.assignments.create() && user?.subAccountUID && (
                        <button
                            onClick={() => navigate('/assignments/new')}
                            className="btn-premium"
                        >
                            <PlusCircle size={18} />
                            <span>New Assignment</span>
                        </button>
                    )}
                </div>
            </div>

            <ContextWarning title={`Select ${SUB_ACCOUNT_LABEL.withArticle} to create or edit assignments`} message={`You're viewing all ${ACCOUNT_LABEL.pluralLower}. To create, edit, or delete an assignment, select ${SUB_ACCOUNT_LABEL.withArticle} from the switcher in the bottom left`} />

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-2">
                <div className="flex p-1.5 glass-panel !bg-base-content/5 rounded-xl w-full sm:w-fit overflow-x-auto no-scrollbar scroll-smooth">
                    {statusTabs.map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-black uppercase whitespace-nowrap transition-all duration-300 ${activeTab === tab ? 'bg-primary text-primary-content shadow-lg scale-105' : 'text-base-content/40 hover:text-primary hover:bg-base-100'}`}
                        >
                            {tabLabels[tab]}
                            {tabCounts[tab] > 0 && (
                                <span className={`px-2 py-0.5 rounded-lg text-xs font-black ${activeTab === tab ? 'bg-primary-content/20 text-primary-content' : 'bg-base-content/10 text-base-content/60'}`}>
                                    {tabCounts[tab]}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-3 w-full lg:w-auto relative">
                    <div className="relative flex-1 lg:w-60">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/20" size={18} />
                        <input
                            type="text"
                            placeholder="Search assignments..."
                            value={filters.search}
                            onChange={(e) => {
                                setFilters(f => ({ ...f, search: e.target.value }));
                                if (e.target.value.trim().length >= 3 || e.target.value.trim().length === 0) {
                                    setSearchError('');
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && filters.search.trim().length > 0 && filters.search.trim().length < 3) {
                                    setSearchError('Search must be at least 3 characters');
                                }
                            }}
                            className={`w-full bg-base-100 border rounded-xl py-2.5 pl-10 pr-10 text-sm focus:outline-none transition-all font-bold placeholder:text-base-content/20 shadow-sm ${searchError ? 'border-error ring-1 ring-error/20' : 'border-base-content/10 focus:border-primary/50'}`}
                        />
                        {searchError && (
                            <div className="absolute top-full left-0 mt-1 text-[10px] text-error font-black uppercase tracking-widest">{searchError}</div>
                        )}
                        {filters.search && (
                            <button
                                onClick={() => setFilters(f => ({ ...f, search: '' }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/20 hover:text-error"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <button
                        onClick={() => {
                            if (!showFilters) {
                                setTempFilters({ workflow: filters.workflow, date: filters.date, customerReferenceNumber: filters.customerReferenceNumber });
                            }
                            setShowFilters(!showFilters);
                        }}
                        className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-black uppercase tracking-widest transition-all shadow-sm ${showFilters || filters.workflow || filters.date ? 'bg-primary text-primary-content border-primary' : 'bg-base-100 border-base-content/10 text-base-content/60 hover:bg-base-content/5'}`}
                    >
                        <Filter size={16} fill={filters.workflow || filters.date ? 'currentColor' : 'none'} />
                        <span>Filters</span>
                    </button>

                    {showFilters && (
                        <div className="absolute top-full right-0 mt-3 w-72 bg-base-100 border border-base-content/10 rounded-2xl shadow-2xl p-5 z-20 animate-in fade-in slide-in-from-top-2 duration-200">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="font-black text-xs uppercase tracking-widest text-base-content/40">Advanced Filters</h4>
                                <button onClick={() => {
                                    setTempFilters({ workflow: '', date: '', customerReferenceNumber: '' });
                                    setFilters(f => ({ ...f, workflow: '', date: '', customerReferenceNumber: '' }));
                                    setShowFilters(false);
                                }} className="text-[10px] font-black uppercase text-primary hover:underline">Clear All</button>
                            </div>

                            <div className="space-y-4">
                                <div className="relative" ref={workflowRef}>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-1.5 block px-1">Workflow</span>
                                    <button
                                        type="button"
                                        onClick={() => setIsWorkflowDropdownOpen(!isWorkflowDropdownOpen)}
                                        className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-2 bg-base-content/5 border border-base-content/5 text-sm font-bold focus:border-primary/30 outline-none cursor-pointer group transition-all"
                                    >
                                        <span className="truncate flex-1 text-left">
                                            {tempFilters.workflow
                                                ? workflows.find(w => w.id === tempFilters.workflow)?.name || 'Unknown Workflow'
                                                : 'All Workflows'}
                                        </span>
                                        <ChevronRight size={14} className={`text-base-content/20 group-hover:text-primary transition-transform ${isWorkflowDropdownOpen ? 'rotate-90' : ''}`} />
                                    </button>

                                    {isWorkflowDropdownOpen && (
                                        <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[30] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col max-h-64">
                                            {/* Search bar */}
                                            <div className="px-3 py-2 border-b border-base-content/10 flex-shrink-0">
                                                <div className="relative">
                                                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/30" />
                                                    <input
                                                        ref={workflowSearchRef}
                                                        type="text"
                                                        value={workflowSearch}
                                                        onChange={e => setWorkflowSearch(e.target.value)}
                                                        placeholder="Search workflows..."
                                                        className="w-full pl-7 pr-3 py-1.5 text-xs bg-base-200 border border-base-content/10 rounded-lg outline-none focus:border-primary text-base-content placeholder-base-content/30"
                                                        autoFocus
                                                    />
                                                    {workflowSearch && (
                                                        <button type="button" onClick={() => setWorkflowSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content">
                                                            <X size={11} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="overflow-y-auto no-scrollbar flex-1">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setTempFilters(f => ({ ...f, workflow: '' }));
                                                        setIsWorkflowDropdownOpen(false);
                                                        setWorkflowSearch('');
                                                    }}
                                                    className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 flex items-center gap-2 group ${!tempFilters.workflow ? 'bg-primary/5' : ''}`}
                                                >
                                                    <span className={`text-[10px] font-black uppercase tracking-widest ${!tempFilters.workflow ? 'text-primary' : 'text-base-content/30 group-hover:text-primary'}`}>All Workflows</span>
                                                    {!tempFilters.workflow && <CheckCircle size={12} className="text-primary ml-auto" />}
                                                </button>

                                                {workflows
                                                    .filter(w =>
                                                        w.name.toLowerCase().includes(workflowSearch.toLowerCase())
                                                    )
                                                    .sort((a, b) =>
                                                        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                                                    )
                                                    .map((w) => (
                                                        <button
                                                            key={w.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setTempFilters(f => ({ ...f, workflow: w.id }));
                                                                setIsWorkflowDropdownOpen(false);
                                                                setWorkflowSearch('');
                                                            }}
                                                            className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group ${tempFilters.workflow === w.id ? 'bg-primary/5' : ''
                                                                }`}
                                                        >
                                                            <span
                                                                className={`text-xs font-bold ${tempFilters.workflow === w.id
                                                                    ? 'text-primary'
                                                                    : 'text-base-content group-hover:text-primary'
                                                                    }`}
                                                            >
                                                                {w.name}
                                                            </span>

                                                            {tempFilters.workflow === w.id && (
                                                                <CheckCircle size={12} className="text-primary ml-auto" />
                                                            )}
                                                        </button>
                                                    ))}
                                                {workflows.filter(w => w.name.toLowerCase().includes(workflowSearch.toLowerCase())).length === 0 && (
                                                    <div className="px-4 py-6 text-center">
                                                        <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">No workflows found</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="filter-date" className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-1.5 block px-1">Created Date</label>
                                    <input
                                        id="filter-date"
                                        type="date"
                                        value={tempFilters.date}
                                        onChange={(e) => setTempFilters(f => ({ ...f, date: e.target.value }))}
                                        onClick={(e) => e.target.showPicker?.()}
                                        className="w-full bg-base-content/5 border border-base-content/5 rounded-xl px-4 py-2 text-sm font-bold focus:outline-none focus:border-primary/30 transition-all text-base-content/60 cursor-pointer"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="filter-customer-ref" className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-1.5 block px-1">{resolveCustomerReferenceLabel(workflows.find((workflow) => workflow.id === tempFilters.workflow))}</label>
                                    <input
                                        id="filter-customer-ref"
                                        type="text"
                                        value={tempFilters.customerReferenceNumber}
                                        placeholder={`${resolveCustomerReferenceLabel(workflows.find((workflow) => workflow.id === tempFilters.workflow))}`}
                                        onChange={(e) => setTempFilters(f => ({ ...f, customerReferenceNumber: e.target.value }))}
                                        className="w-full bg-base-content/5 border border-base-content/5 rounded-xl px-4 py-2 text-sm font-bold focus:outline-none focus:border-primary/30 transition-all"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={() => {
                                    setFilters(f => ({ ...f, workflow: tempFilters.workflow, date: tempFilters.date, customerReferenceNumber: tempFilters.customerReferenceNumber }));
                                    setShowFilters(false);
                                }}
                                className="w-full mt-6 py-3 bg-primary text-primary-content rounded-xl font-black uppercase tracking-widest text-xs hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all"
                            >
                                Apply Filters
                            </button>
                        </div>
                    )}

                </div>
            </div>

            {error && (
                <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg mb-4 text-sm font-bold">{error}</div>
            )}

            {loading ? (
                <div className="flex flex-col items-center justify-center py-32 space-y-4">
                    <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-base-content/40 font-black uppercase tracking-widest text-xs animate-pulse">Loading assignments...</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-32 glass-panel !bg-base-100/30 rounded-xl border-dashed border-2 border-base-content/10">
                    <div className="w-20 h-20 bg-base-200 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                        <span className="text-4xl text-base-content/30">📋</span>
                    </div>
                    <h3 className="text-2xl font-black text-base-content mb-2 tracking-tight">No assignments found</h3>
                    <p className="text-base-content/50 max-w-xs mx-auto font-bold uppercase tracking-widest text-[10px]">
                        {activeTab === 'All' && !filters.search && !filters.workflow && !filters.date
                            ? 'Create your first assignment'
                            : `Try adjusting your search or filters`}
                    </p >
                </div >
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filtered.map(assignment => (
                        <div
                            key={assignment.id}
                            className="card-premium rounded-xl group relative"
                        >
                            <div className="absolute top-4 right-3 flex items-center gap-0.5 z-10 opacity-60 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/assignments/${assignment.id}`);
                                    }}
                                    className="p-1.5 text-base-content/30 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                    title="View assignment"
                                >
                                    <Eye size={14} />
                                </button>
                                {assignment.status === 'created' && can.assignments.update() && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/assignments/${assignment.id}/edit`);
                                        }}
                                        className="p-1.5 text-base-content/30 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                        title="Edit assignment"
                                    >
                                        <Edit2 size={14} />
                                    </button>
                                )}
                                {(assignment.status === 'created' || assignment.status === 'expired') && can.assignments.delete() && (
                                    <button
                                        onClick={(e) => handleDeleteClick(e, assignment)}
                                        className="p-1.5 text-base-content/30 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                                        title="Delete assignment"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>

                            <div className="flex flex-col h-full">
                                <div className="flex items-start gap-3 mb-5">
                                    <div className="w-11 h-11 bg-primary/5 rounded-xl flex items-center justify-center text-lg font-black text-primary border border-primary/10 group-hover:scale-105 transition-transform duration-300 shrink-0">
                                        {assignment.submitterName?.charAt(0) || 'U'}
                                    </div>
                                    <div className="min-w-0 flex-1 pr-20">
                                        <h3 className="font-black text-base-content text-base leading-tight truncate group-hover:text-primary transition-colors tracking-tight mb-1">
                                            {assignment.submitterName}
                                        </h3>
                                        <StatusBadge status={assignment.status} />
                                    </div>
                                </div>
                                {assignment.customerReferenceNumber && (
                                    <div className="mb-4">
                                        <div className="flex items-center gap-1.5 w-full">
                                            <span className="text-[11px] font-black text-base-content/30 uppercase tracking-[.15em] whitespace-nowrap truncate max-w-[60%]" title={resolveCustomerReferenceLabel(assignment)}>{resolveCustomerReferenceLabel(assignment)}:</span>
                                            <span className="text-[11px] font-bold text-primary truncate w-[40%]" title={assignment.customerReferenceNumber}>{assignment.customerReferenceNumber}</span>
                                        </div>
                                    </div>
                                )}
                                {assignment.systemReferenceNumber && (
                                    <div className="mb-4">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[11px] font-black text-base-content/30 uppercase tracking-[.15em]">SYSTEM REF:</span>
                                            <span className="text-[11px] font-bold text-primary">{assignment.systemReferenceNumber}</span>
                                        </div>
                                    </div>
                                )}
                                <div className="mb-6">
                                    <p className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em] mb-1">Workflow</p>
                                    <p className="text-base font-bold text-base-content/80 truncate">{assignment.workflowName}</p>
                                </div>

                                <div className="mt-auto space-y-3">
                                    <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-widest">
                                        <span className="text-base-content/20">{assignment.completedItems}/{assignment.totalItems} Complete</span>
                                        <span className="text-primary/60">{Math.round((assignment.completedItems / (assignment.totalItems || 1)) * 100)}%</span>
                                    </div>

                                    <div className="w-full bg-base-content/5 rounded-full h-1 overflow-hidden">
                                        <div
                                            className="bg-primary h-full rounded-full transition-all duration-1000 shadow-[0_0_8px_hsl(var(--p)/0.3)]"
                                            style={{ width: `${(assignment.completedItems / (assignment.totalItems || 1)) * 100}%` }}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t border-base-content/5 text-[10px] font-black text-base-content/20 uppercase tracking-[0.2em]">
                                        <div className="flex items-center gap-1.5">
                                            <Clock size={12} />
                                            <span>{formatDate(assignment.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            <div className="px-4">
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                    loading={loading}
                />
            </div>

            {/* Delete Confirmation Dialog */}
            <Modal
                isOpen={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete Assignment"
            >
                <div className="mb-6">
                    <p className="text-base-content/60 text-sm">
                        Are you sure you want to delete the assignment for {' '}
                        <strong className="text-base-content">{deleteConfirm?.submitterName}</strong>? This action cannot be undone. All associated media files and data will be permanently deleted.
                    </p>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => setDeleteConfirm(null)}
                        className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
                        disabled={deleting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => handleDelete(deleteConfirm.id)}
                        className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete Assignment'}
                    </button>
                </div>
            </Modal>
        </div >
    );
};

export default Assignments;
